import React, { useMemo, useCallback, useState } from "react";
import Footer from "../Common/Footer";
import Nav from "../Common/Nav";
import "react-confirm-alert/src/react-confirm-alert.css";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DocumentMeta from "react-document-meta";
import {
  useLaunchpadListingQuery,
  useLaunchpadBuyMutation,
  useLaunchpadMyPurchasesQuery,
} from "../../redux/userApi";
import config from "../../utils/config";
import Swal from "sweetalert2";
import { Decimal_util } from "../../utils/Dec.util";
const { imageUrl } = config;

const meta = {
  title: "LaunchPad | Green Crypto  Exchange ",
  description:
    "Green Crypto  Exchange Live Makes The Buying And Selling Of Crypto Coin Way More Effective And Profitable. We Are A Low Cost Cyrpto Exchange In India Like Bitcoin And Dogecoin.",
  canonical: "https://Green Crypto  Exchange.live/buy-sell-crypto-online",
  meta: {
    charset: "utf-8",
    name: {
      keywords:
        "BTC Exchange, Crypto Trading Platform, Best Crypto Converter, Indian Crypto Exchange",
    },
  },
};

const LaunchPad = () => {
  const { data: launchpadData, refetch } = useLaunchpadListingQuery();
  const [launchpadBuy] = useLaunchpadBuyMutation();
  const isLoggedIn = Boolean(localStorage.getItem("jwtToken"));
  const [historyPage, setHistoryPage] = useState(1);
  const historyLimit = 10;
  const {
    data: purchaseData,
    refetch: refetchPurchases,
    isFetching: isFetchingPurchases,
  } = useLaunchpadMyPurchasesQuery(
    { page: historyPage, limit: historyLimit },
    { skip: !isLoggedIn },
  );
  const [buyingId, setBuyingId] = useState(null);

  // today's date in yyyy-mm-dd (same as before)
  const formattedDate = useMemo(() => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, "0");
    const day = String(currentDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const modifyText = useCallback((text) => {
    if (!text) return "";
    return text.length <= 8 ? text : text.substr(0, 8);
  }, []);

  const parseLaunchpadDate = useCallback((value, options = {}) => {
    if (!value) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;

    if (trimmed.includes("T")) {
      const isoDate = new Date(trimmed);
      return Number.isNaN(isoDate.getTime()) ? null : isoDate;
    }

    if (trimmed.includes("/")) {
      const [datePart, timePart] = trimmed.split(" ");
      const [day, month, year] = datePart.split("/").map(Number);
      if (!day || !month || !year) return null;
      let hours = 0;
      let minutes = 0;
      let seconds = 0;
      if (timePart) {
        const [h, m, s] = timePart.split(":").map(Number);
        hours = h || 0;
        minutes = m || 0;
        seconds = s || 0;
      }
      const parsed = new Date(year, month - 1, day, hours, minutes, seconds);
      if (Number.isNaN(parsed.getTime())) return null;
      if (options.endOfDay && !timePart) {
        parsed.setHours(23, 59, 59, 999);
      }
      return parsed;
    }

    if (trimmed.includes("-")) {
      const [datePart, timePart] = trimmed.split(" ");
      const [year, month, day] = datePart.split("-").map(Number);
      if (!day || !month || !year) return null;
      let hours = 0;
      let minutes = 0;
      let seconds = 0;
      if (timePart) {
        const [h, m, s] = timePart.split(":").map(Number);
        hours = h || 0;
        minutes = m || 0;
        seconds = s || 0;
      }
      const parsed = new Date(year, month - 1, day, hours, minutes, seconds);
      if (Number.isNaN(parsed.getTime())) return null;
      if (options.endOfDay && !timePart) {
        parsed.setHours(23, 59, 59, 999);
      }
      return parsed;
    }

    return null;
  }, []);

  const generateIdempotencyKey = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }, []);

  const handleBuy = useCallback(
    async (launchpad) => {
      if (!localStorage.getItem("jwtToken")) {
        toast.dismiss();
        toast.error("Please login to buy");
        return;
      }

      const { value: payAmount } = await Swal.fire({
        title: `Buy ${launchpad.short_name}`,
        input: "text",
        inputLabel: "Pay amount (USDT)",
        inputPlaceholder: "100.00",
        showCancelButton: true,
        confirmButtonText: "Buy",
        inputValidator: (value) => {
          if (!value) return "Pay amount is required";
          const numeric = Number(value);
          if (!Number.isFinite(numeric) || numeric <= 0) {
            return "Enter a valid amount";
          }
          return null;
        },
      });

      if (!payAmount) {
        return;
      }

      setBuyingId(launchpad.id);
      try {
        await launchpadBuy({
          launchpadId: launchpad.id,
          payAmount,
          idempotencyKey: generateIdempotencyKey(),
        }).unwrap();
        toast.dismiss();
        toast.success("Purchase successful");
        refetch();
        if (refetchPurchases) {
          refetchPurchases();
        }
      } catch (error) {
        const message =
          error?.data?.message ||
          error?.data?.debug ||
          "Unable to complete purchase";
        toast.dismiss();
        toast.error(message);
      } finally {
        setBuyingId(null);
      }
    },
    [generateIdempotencyKey, launchpadBuy, refetch],
  );

  // Single reusable renderer for a launch card (keeps markup identical)
  const renderCard = useCallback(
    (value) => {
      const token = (value.token_name || "").toUpperCase();
      const short = (value.short_name || "").toUpperCase();
      const longToken = token && token.length > 8;
      const startDate = parseLaunchpadDate(value.ico_start_date);
      const endDate = parseLaunchpadDate(value.ico_end_date, {
        endOfDay: true,
      });
      const isSaleStarted = startDate
        ? Date.now() >= startDate.getTime()
        : false;
      const isSaleEnded = endDate ? Date.now() > endDate.getTime() : false;
      return (
        <div className="col-lg-3 col-md-4 col-sm-6 col-12" key={value.id}>
          <div className="launch-box">
            <div className="img-outer">
              <a>
                <img src={imageUrl + value.logo} alt="" className="img-fluid" />
              </a>
            </div>
            <div className="content-outer">
              <h4>
                {longToken ? (
                  <OverlayTrigger
                    key={value.id}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-${value.id}`}>
                        <strong>{token}</strong>
                      </Tooltip>
                    }
                  >
                    <a>
                      {modifyText(token)}
                      ...
                    </a>
                  </OverlayTrigger>
                ) : !token ? (
                  "-"
                ) : (
                  token
                )}{" "}
                ({short})
              </h4>

              <p className="supply-total d-flex justify-content-between position-relative">
                Session Supply:{" "}
                <b>
                  {Number(value.tokens_sold || 0)?.toFixed(2)} /{" "}
                  {value.session_supply}
                </b>
              </p>
              <p className="supply-total d-flex justify-content-between position-relative">
                ICO Price:{" "}
                <b>{Decimal_util.fixedtillString(value.ico_price, 8)}</b>
              </p>

              <div className="start-end">
                <p className="start text-success d-flex justify-content-between">
                  &#x1F551; Start{" "}
                  <b className="text-white">{value.ico_start_date}</b>
                </p>
                <p className="end text-danger d-flex justify-content-between">
                  &#x1F551; End{" "}
                  <b className="text-white">{value.ico_end_date}</b>
                </p>
              </div>

              {/* preserved listing-outer markup (was commented in some places) */}
              {!isSaleStarted ? (
                <div className="listing-outer">
                  <span className="text-warning">Sale not started</span>
                </div>
              ) : isSaleEnded ? (
                <div className="listing-outer">
                  <span className="text-danger">Sale ended</span>
                </div>
              ) : (
                <div className="listing-outer">
                  <button
                    className="btn btn_man"
                    onClick={() => handleBuy(value)}
                    disabled={buyingId === value.id}
                  >
                    {buyingId === value.id ? "Buying..." : "Buy"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    },
    [buyingId, handleBuy, imageUrl, modifyText, parseLaunchpadDate],
  );

  // memoized list rendering (keeps rendering fast)
  const allHtml = useMemo(() => {
    const arr = launchpadData?.data;
    if (!arr || !arr.length) {
      return (
        <div className="col-12">
          <div className="launch-box">
            <center>
              <h3>No Data</h3>
            </center>
          </div>
        </div>
      );
    }
    return arr.map(renderCard);
  }, [launchpadData, renderCard]);

  const formatDecimal = useCallback((value) => {
    if (value === null || value === undefined || value === "") return "0";
    const str = String(value);
    if (!str.includes(".")) return str;
    const [intPart, fracPart = ""] = str.split(".");
    const trimmedFrac = fracPart.slice(0, 8).replace(/0+$/, "");
    return trimmedFrac ? `${intPart}.${trimmedFrac}` : intPart;
  }, []);

  const historyRows = useMemo(() => {
    const rows = purchaseData?.data;
    if (!rows || !rows.length) {
      return (
        <tr>
          <td colSpan="6" className="text-center">
            {isFetchingPurchases ? "Loading..." : "No purchase history"}
          </td>
        </tr>
      );
    }
    return rows.map((item) => (
      <tr key={item.id}>
        <td>{item.created_at}</td>
        <td>{(item.short_name || item.token_symbol || "").toUpperCase()}</td>
        <td>
          {formatDecimal(item.pay_amount)} {item.pay_symbol}
        </td>
        <td>{formatDecimal(item.token_amount)}</td>
        <td>{formatDecimal(item.ico_price)}</td>
        <td>{item.status === "ACTIVE" ? "Completed" : "Pending"}</td>
      </tr>
    ));
  }, [formatDecimal, isFetchingPurchases, purchaseData]);

  const checkLogin = useCallback(() => {
    if (!localStorage.getItem("jwtToken")) {
      toast.dismiss();
      toast.error("Please login to apply");
      return false;
    } else {
      toast.dismiss();
      window.location.href = "/launchPadForm";
    }
  }, []);

  const showHeaderPart = useCallback(() => {
    const token = localStorage.getItem("theme");
    if (token == "false") {
      return (
        <img
          src="assets/img/banner-right-img1.gif"
          alt=""
          className="img-fluid"
        />
      );
    }
    if (token == "true") {
      return (
        <img
          src="assets/img/banner-right-img2.png"
          alt=""
          className="img-fluid"
        />
      );
    }
    return null;
  }, []);

  return (
    <div>
      <DocumentMeta {...meta}>
        <Nav graphColor="" />
        <header className="home position-relative launch-home" id="home">
          <div className="container">
            <div className="home-outer launchpaid-outer launchpaid-off">
              <div className="banner-left">
                <h1>LAUNCHPAD</h1>
                <h2>
                  Unlock the future of finance with our exclusive crypto
                  exchange launchpad.
                </h2>
                <p>
                  Are you ready to discover the next big thing in the world of
                  digital assets? Our top-tier launchpad is the gateway to
                  revolutionary projects that could transform the crypto
                  landscape.
                </p>

                <div className="banner-btn-outer">
                  <button className="btn btn_man" onClick={checkLogin}>
                    Apply for Launchpad
                  </button>
                </div>
              </div>
              <div className="banner-right">
                <img
                  src="assets/img/launch-right.png"
                  alt=""
                  className="img-fluid"
                />
              </div>
            </div>
          </div>
        </header>

        <div className="launch-main">
          <div className="container">
            <h2>Listed Launchpads</h2>
            <div className="launch-outer">
              <div className="launch-inner">
                <div className="row">{allHtml}</div>
              </div>
            </div>

            {isLoggedIn ? (
              <div className="launch-history mt-4">
                <h2>My Launchpad History</h2>
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Token</th>
                        <th>Pay Amount</th>
                        <th>Token Amount</th>
                        <th>ICO Price</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>{historyRows}</tbody>
                  </table>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button
                    className="btn btn-secondary"
                    onClick={() =>
                      setHistoryPage((prev) => Math.max(prev - 1, 1))
                    }
                    disabled={historyPage <= 1}
                  >
                    Previous
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setHistoryPage((prev) => prev + 1)}
                    disabled={
                      purchaseData?.pagination?.total_pages
                        ? historyPage >= purchaseData.pagination.total_pages
                        : false
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}

            <div className="launch-faq">
              <div className="section-heading">
                <h3>FAQ</h3>
              </div>
              <div className="accordion" id="faq">
                <div className="card">
                  <div className="card-header" id="faqhead1">
                    <a
                      href="#"
                      className="btn btn-header-link"
                      data-toggle="collapse"
                      data-target="#faq1"
                      aria-expanded="true"
                      aria-controls="faq1"
                    >
                      What is a crypto exchange launchpad?
                    </a>
                  </div>
                  <div
                    id="faq1"
                    className="collapse show"
                    aria-labelledby="faqhead1"
                    data-parent="#faq"
                  >
                    <div className="card-body">
                      <p>
                        It's a platform that allows promising crypto projects to
                        launch their tokens directly on our exchange. This gives
                        them exposure to our wide user base.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header" id="faqhead2">
                    <a
                      href="#"
                      className="btn btn-header-link collapsed"
                      data-toggle="collapse"
                      data-target="#faq2"
                      aria-expanded="false"
                      aria-controls="faq2"
                    >
                      Why should I participate in launchpad sales?
                    </a>
                  </div>

                  <div
                    id="faq2"
                    className="collapse"
                    aria-labelledby="faqhead2"
                    data-parent="#faq"
                  >
                    <div className="card-body">
                      <p>
                        Early access to vetted, high-quality projects means you
                        can get in on the ground floor. Think huge upside
                        potential if the project takes off!
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header" id="faqhead3">
                    <a
                      href="#"
                      className="btn btn-header-link collapsed"
                      data-toggle="collapse"
                      data-target="#faq3"
                      aria-expanded="false"
                      aria-controls="faq3"
                    >
                      How do I join an upcoming launchpad sale?
                    </a>
                  </div>

                  <div
                    id="faq3"
                    className="collapse"
                    aria-labelledby="faqhead3"
                    data-parent="#faq"
                  >
                    <div className="card-body">
                      <p>
                        It's simple - just complete the KYC process and reserve
                        your spot. Act fast, as seats fill up quickly for the
                        most anticipated launches.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        {/* <ToastContainer /> */}
      </DocumentMeta>
    </div>
  );
};

export default LaunchPad;
